import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import type { PreviewSignalResponse } from '@/entities/signal';
import { cn } from '@/lib/cn';
import { DataTable, DataTableEmpty } from '@/shared/ui/data-table';

import { SignalBadge } from '../../signals';

type PreviewItem = PreviewSignalResponse[number];

type TabType = 'matched' | 'not_matched';

const isMatchedData = (change: PreviewItem['change']) =>
  change === 'ADDED' || change === 'SUSPENDED';

const isPrevMatchedData = (change: PreviewItem['change']) =>
  change === 'SUSPENDED' || change === 'REMOVED';

const isAfterMatchedData = (change: PreviewItem['change']) =>
  change === 'ADDED' || change === 'SUSPENDED';

const createColumns = (
  signalTitle: string,
  originalTitle: string,
): ColumnDef<PreviewItem, any>[] => [
  {
    accessorKey: 'name',
    header: 'Issue',
    meta: { className: 'flex-1 px-6' },
  },
  {
    id: 'before',
    header: 'Before',
    size: 160,
    cell: ({ row }) => {
      const change = row.original.change;
      if (isPrevMatchedData(change)) {
        return <SignalBadge title={originalTitle} tooltip={originalTitle} />;
      }
      return <span className="text-muted-foreground">-</span>;
    },
  },
  {
    id: 'after',
    header: 'After',
    size: 160,
    cell: ({ row }) => {
      const change = row.original.change;
      if (isAfterMatchedData(change)) {
        return <SignalBadge title={signalTitle} tooltip={signalTitle} />;
      }
      return <span className="text-muted-foreground">-</span>;
    },
  },
];

type SignalPreviewTableProps = {
  data: PreviewSignalResponse;
  signalTitle: string;
  originalTitle: string;
  isUpdateMode: boolean;
};

export function SignalPreviewTable({
  data,
  signalTitle,
  originalTitle,
  isUpdateMode,
}: SignalPreviewTableProps) {
  const [activeTab, setActiveTab] = useState<TabType>('matched');

  // 데이터 필터링 (mode별 분기)
  const matchedData = useMemo(
    () => data.filter((item) => isMatchedData(item.change)),
    [data, isUpdateMode],
  );

  const notMatchedData = useMemo(
    () => data.filter((item) => !isMatchedData(item.change)),
    [data, isUpdateMode],
  );

  const isMatchedTab = activeTab === 'matched';
  const displayData = isMatchedTab ? matchedData : notMatchedData;

  const columns = useMemo(
    () => createColumns(signalTitle, originalTitle),
    [signalTitle, originalTitle, isUpdateMode],
  );

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* 헤더 */}
      <div className="flex shrink-0 flex-col gap-1">
        <p className="text-sm font-semibold">Previewing recent issues</p>
        <p className="text-xs text-muted-foreground">
          This preview shows the most recent issues. Some changes may only
          appear after saving.
        </p>
      </div>

      {/* 탭 */}
      <div className="flex shrink-0 gap-1 rounded-lg bg-white p-1">
        <button
          type="button"
          onClick={() => setActiveTab('matched')}
          className={cn(
            'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            activeTab === 'matched'
              ? 'bg-secondary text-secondary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Matched ({matchedData.length})
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('not_matched')}
          className={cn(
            'flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
            activeTab === 'not_matched'
              ? 'bg-secondary text-secondary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Not matched ({notMatchedData.length})
        </button>
      </div>

      {/* 테이블 */}
      <div className="overflow-auto rounded-lg bg-white ">
        <DataTable
          columns={columns}
          data={displayData}
          headerClassName="sticky top-0 z-10 bg-white"
          size="sm"
          emptyState={
            <DataTableEmpty
              title={isMatchedTab ? 'No matching issues found' : 'All issues matched'}
            />
          }
        />
      </div>
    </div>
  );
}
